import "normalize.css/normalize.css";
import "@blueprintjs/icons/lib/css/blueprint-icons.css";
import "@blueprintjs/core/lib/css/blueprint.css";
import "@blueprintjs/popover2/lib/css/blueprint-popover2.css";

import { assertNever } from "assert-never";
import React from "react";
import ReactDOM from "react-dom";
import { FaDumpsterFire } from "react-icons/fa";
import { Shortcuts } from "shortcuts";

import { appGetCodeDiagnosticsInEditorAtPoint, CodeDiagnosticId } from "./CodeDiagnostics";
import { ColorTheme, loadColorTheme, saveColorTheme } from "./ColorTheme";
import CodeDiagnosticsPanel from "./components/CodeDiagnosticsPanel";
import { EditorContextPopup } from "./components/EditorContextPopup/EditorContextPopup";
import { createEditorPanel } from "./components/EditorPanelFactory";
import EditorTabs from "./components/EditorTabs";
import FileTreeViewPanel from "./components/FileTreeViewPanel";
import GlobalNotificationBanner from "./components/GlobalNotificationBanner";
import { IEditorPanel } from "./components/IEditorPanel";
import LoadingDialog from "./components/LoadingDialog";
import Statusbar, { mkStatusbarProps, StatusbarProps } from "./components/Statusbar";
import { Terminal } from "./components/Terminal";
import Toolbar from "./components/Toolbar";
import * as ToolsPanelTabs from "./components/ToolsPanelTabs";
import { showCrashDialog } from "./CrashHandling";
import { DEMO_MODE, DEMO_MODE_DISABLED_TEXT } from "./DemoMode";
import { Panic } from "./Errors/Panic";
import { Command, makeCommand, texprCommand } from "./gen/AppCommands";
import {
  AppEvent,
  EditorFileBufferChanged,
  EditorFileOpened,
  EditorFileSyntaxHighlighting,
  EditorTabClosed,
  FileTreeViewRefreshed,
  InfoRequestRegistration,
  InfoRequestResultEvent,
  PluginCodeDiagnosticsUpdate,
  TerminalCreated,
  TerminalOutputEvent,
  texprAppEvent
} from "./gen/AppEvents";
import {
  ClientState,
  EditorFileId,
  PluginState,
  TerminalId,
  texprClientState
} from "./gen/ClientState";
import { CodeDiagnostic } from "./gen/CodeDiagnostics";
import { FileTreeView } from "./gen/FileTreeView";
import { InfoRequestId } from "./gen/InfoRequest";
import { RESOLVER } from "./gen/resolver";
import { createJsonBinding } from "./gen/runtime/json";
import { LineCol } from "./gen/SrcLoc";
import { KeyPressCommand, makeTerminalCommand, TerminalSize, TermSurface } from "./gen/Terminal";
import { createWorkspaceWindow } from "./WorkspaceWindow";

const commandJsonBinding = createJsonBinding(RESOLVER, texprCommand());
const clientStateJsonBinding = createJsonBinding(RESOLVER, texprClientState());
const appEventJsonBinding = createJsonBinding(RESOLVER, texprAppEvent());

function getWebsocketUrl(): string {
  if (window.location.hash.length > 1) {
    return window.location.hash.substr(1);
  } else {
    return "ws://localhost:9610/client-session";
  }
}

export function startHexGrip(): void {
  console.log("startHexGrip");

  const websocketUrl = getWebsocketUrl();
  console.log("Connecting to:", websocketUrl);

  const ws = new WebSocket(websocketUrl);

  const loadingDialogRoot = document.createElement("div");
  document.body.appendChild(loadingDialogRoot);

  const renderLoadingDialog = (message: string): void => {
    ReactDOM.render(<LoadingDialog title="Connecting" message={message} />, loadingDialogRoot);
  };

  const closeLoadingDialog = (): void => {
    ReactDOM.unmountComponentAtNode(loadingDialogRoot);
    if (loadingDialogRoot.parentElement !== null) {
      loadingDialogRoot.parentElement.removeChild(loadingDialogRoot);
    }
  };

  renderLoadingDialog("Establishing Websocket connection");

  ws.binaryType = "arraybuffer";
  ws.onopen = () => {
    console.log("WebSocket: Connected");
    renderLoadingDialog("Connected, waiting for session");
  };
  ws.onerror = (err) => {
    closeLoadingDialog();
    showCrashDialog({
      error: new Error(
        `Error connecting to Websocket (Check DevTools Console for more info):\n${websocketUrl}`
      )
    });
    ws.close();
  };
  ws.onclose = (e) => {
    console.log("WebSocket: Close", e.code, e);
    closeLoadingDialog();
    const errorMessage = `Websocket Connection closed:\n${JSON.stringify({
      code: e.code,
      reason: e.reason,
      wasClean: e.wasClean
    })}`;
    if (app === null) {
      showCrashDialog({
        error: new Error(errorMessage)
      });
    } else {
      hexgripShowGlobalNotificationBanner(
        app,
        <GlobalNotificationBanner icon={FaDumpsterFire} color="#a00">
          <strong>ERROR:</strong> The app crashed! Please copy your work and save it locally.{" "}
          <small>
            <em>Reason:</em> {errorMessage}
          </small>
        </GlobalNotificationBanner>
      );
    }
  };
  let app: HexGripApp | null = null;
  ws.onmessage = (msg) => {
    console.log("WebSocket: Message", msg);
    if (app === null) {
      closeLoadingDialog();

      // TODO Catch errors below and call 'showCrashDialog'

      const decoder = new TextDecoder();
      const clientState = clientStateJsonBinding.fromJsonE(JSON.parse(decoder.decode(msg.data)));
      console.log("clientState", clientState);
      app = initHexGripApp(clientState, (cmd) => {
        const encoder = new TextEncoder();
        const encoded = JSON.stringify(commandJsonBinding.toJson(cmd));
        const msg = encoder.encode(encoded);
        ws.send(msg);
      });
    } else {
      const decoder = new TextDecoder();
      const appEvent = (() => {
        try {
          return appEventJsonBinding.fromJsonE(JSON.parse(decoder.decode(msg.data)));
        } catch (err) {
          // TODO Catch errors below and call 'showCrashDialog'
          console.warn("Error parsing event:", err);
          return null;
        }
      })();
      if (appEvent !== null) {
        console.log("appEvent", appEvent);
        hexGripAppEvent(app, appEvent);
      }
    }
  };
}

export interface HexGripApp {
  commandSink: (cmd: Command) => void;
  root: Element;
  shortcuts: Shortcuts;
  colorTheme: ColorTheme;
  nextInfoRequestId: InfoRequestId;
  openEditorContextPopup: OpenEditorContextPopup | null;
  toolbarRoot: Element;
  fileTreeView: FileTreeView;
  fileTreeViewPanelRoot: Element;
  globalNotificationBanner: Element;
  editorTabs: EditorTab[];
  editorTabsRoot: Element;
  toolsPanelTabsRoot: Element;
  activeToolsPanelTab: ToolsPanelTabs.TabIdentifier;
  toolsPanelTerminalsRoot: HTMLElement;
  toolsPanelCodeDiagnosticsRoot: HTMLElement;
  toolsPanelGhciRoot: HTMLElement;
  statusbarRoot: Element;
  statusbarProps: StatusbarProps;
  editorPanel: IEditorPanel;
  activeEditorFileId: EditorFileId | null;
  pluginState: Map<string, PluginState>;
  terminals: Map<TerminalId, Terminal>;
}

interface OpenEditorContextPopup {
  editorContextPopup: EditorContextPopup<CodeDiagnosticId>;
  infoRequestId: InfoRequestId;
  lineCol: LineCol;
}

export interface EditorTab {
  id: EditorFileId;
  filePath: string | null;
}

function renderToolbar(app: HexGripApp): void {
  ReactDOM.render(
    <Toolbar
      currentColorTheme={app.colorTheme}
      onColorThemeClick={(colorTheme) => {
        console.log("onColorThemeClick", colorTheme);
        setColorTheme(app, colorTheme);
      }}
      onRefreshFileTreeViewClick={() => {
        app.commandSink(makeCommand("refreshFileTreeView", {}));
      }}
      onNewFileClick={() => {
        app.commandSink(makeCommand("newFile", {}));
      }}
      onSaveClick={() => {
        if (app.activeEditorFileId !== null) {
          app.commandSink(
            makeCommand("saveFile", {
              id: app.activeEditorFileId
            })
          );
        }
      }}
      onUndoClick={() => {
        app.editorPanel.performUndo();
      }}
      onRedoClick={() => {
        app.editorPanel.performRedo();
      }}
      onCutClick={() => {
        app.editorPanel.performCut();
      }}
      onCopyClick={() => {
        app.editorPanel.performCopy();
      }}
      onPasteClick={() => {
        app.editorPanel.performPaste();
      }}
    />,
    app.toolbarRoot
  );
}

function renderFileTreePanel(app: HexGripApp): void {
  ReactDOM.render(
    <FileTreeViewPanel
      fileTreeView={app.fileTreeView}
      onFileDoubleClick={(path) => {
        const filePath = path.join("/");
        console.log("filePath", filePath);
        const existing = app.editorTabs.find((x) => x.filePath === filePath);
        if (existing !== undefined) {
          hexGripAppSetActiveTab(app, existing.id);
        } else {
          app.commandSink(
            makeCommand("openFile", {
              filePath: filePath
            })
          );
        }
      }}
    />,
    app.fileTreeViewPanelRoot
  );
}

function renderEditorTabs(app: HexGripApp): void {
  ReactDOM.render(
    <EditorTabs
      tabs={app.editorTabs}
      activeTab={app.activeEditorFileId}
      onTabClick={(id: EditorFileId) => {
        hexGripAppSetActiveTab(app, id);
      }}
      onTabCloseClick={(id: EditorFileId) => {
        app.commandSink(
          makeCommand("closeTab", {
            id: id
          })
        );
      }}
    />,
    app.editorTabsRoot
  );
}

function renderToolsPanelTabs(app: HexGripApp): void {
  ReactDOM.render(
    <ToolsPanelTabs.ToolsPanelTabs
      tab={app.activeToolsPanelTab}
      onTabChange={(tab) => {
        hexGripAppSetActiveToolsPanelTab(app, tab);
      }}
      onNewTerminalClick={() => {
        const terminalSize = Terminal.measureSize(app.toolsPanelTerminalsRoot);
        console.log("terminalSize", terminalSize);
        app.commandSink(
          makeCommand("newTerminal", {
            params: {
              path: "/bin/bash",
              argv: [],
              size: terminalSize
            }
          })
        );
      }}
      onCloseTerminalClick={() => {}}
    />,
    app.toolsPanelTabsRoot
  );
}

function renderCodeDiagnosticsPanel(app: HexGripApp): void {
  let codeDiagnostics: CodeDiagnostic[] = [];

  for (const plugin of app.pluginState.values()) {
    codeDiagnostics = codeDiagnostics.concat(plugin.codeDiagnostics);
  }

  if (codeDiagnostics.length > 0) {
    ReactDOM.render(
      <CodeDiagnosticsPanel codeDiagnostics={codeDiagnostics} />,
      app.toolsPanelCodeDiagnosticsRoot
    );
  } else {
    ReactDOM.unmountComponentAtNode(app.toolsPanelCodeDiagnosticsRoot);
  }
}

function renderStatusbar(app: HexGripApp): void {
  ReactDOM.render(<Statusbar {...app.statusbarProps} />, app.statusbarRoot);
}

export function initHexGripApp(
  clientState: ClientState,
  commandSink: (cmd: Command) => void
): HexGripApp {
  const root = document.createElement("div");
  document.body.appendChild(root);

  const workspaceWindow = createWorkspaceWindow();
  root.appendChild(workspaceWindow.root);

  const editorTabs = clientState.openFiles.map((value) => ({
    id: value.id,
    filePath: value.filePath
  }));

  const activeEditorFileId =
    clientState.openFiles.length === 0 ? null : clientState.openFiles[0].id;

  const editorPanel = createEditorPanel(
    workspaceWindow.editor,
    clientState.openFiles,
    activeEditorFileId,
    {
      onCursorPositionChange: (position) => {
        app.statusbarProps.cursorPosition = position;
        renderStatusbar(app);
      },
      onBufferChange: (id, changes) => {
        app.commandSink(
          makeCommand("editorBufferChange", {
            id: id,
            changes: changes
          })
        );
      },
      onEditorContextPopup: (filePath, lineCol, clickPos) => {
        if (app.openEditorContextPopup !== null) {
          app.openEditorContextPopup.editorContextPopup.dispose();
        }

        const editorTab = app.editorTabs.find((x) => x.filePath === filePath);
        if (editorTab === undefined) {
          // TODO ...
        }

        const codeDiagnostics = appGetCodeDiagnosticsInEditorAtPoint(app, filePath, lineCol);

        const infoRequestId = app.nextInfoRequestId;
        app.nextInfoRequestId++;

        const dimissContextPopup = (): void => {
          if (app.openEditorContextPopup === null) {
            throw new Panic("openEditorContextPopup is null", {});
          }
          app.openEditorContextPopup.editorContextPopup.dispose();
          app.openEditorContextPopup = null;
          app.editorPanel.focus();
        };

        app.openEditorContextPopup = {
          infoRequestId: infoRequestId,
          lineCol: lineCol,
          editorContextPopup: new EditorContextPopup(clickPos, codeDiagnostics, {
            onDismiss: dimissContextPopup,
            onCutClick: () => {
              app.editorPanel.performCut();
              dimissContextPopup();
            },
            onCopyClick: () => {
              app.editorPanel.performCopy();
              dimissContextPopup();
            },
            onPasteClick: () => {
              app.editorPanel.performPaste();
              dimissContextPopup();
            }
          })
        };

        app.commandSink(
          makeCommand("infoRequestCommand", {
            id: infoRequestId,
            infoRequest: {
              filePath: filePath,
              location: lineCol
            }
          })
        );
      }
    }
  );

  const app: HexGripApp = {
    commandSink: commandSink,
    root: root,
    shortcuts: new Shortcuts({ capture: true }),
    colorTheme: loadColorTheme(),
    nextInfoRequestId: 1, // TODO Should initialize this from the server state, to prevent re-use
    openEditorContextPopup: null,
    toolbarRoot: workspaceWindow.toolbar,
    fileTreeView: clientState.fileTreeView,
    fileTreeViewPanelRoot: workspaceWindow.sidebar,
    globalNotificationBanner: workspaceWindow.globalNotifactionBanner,
    editorTabs: editorTabs,
    editorTabsRoot: workspaceWindow.editorTabs,
    toolsPanelTabsRoot: workspaceWindow.toolsPanelTabs,
    activeToolsPanelTab: "DIAGNOSTICS",
    toolsPanelTerminalsRoot: workspaceWindow.toolsPanelTerminals,
    toolsPanelCodeDiagnosticsRoot: workspaceWindow.toolsPanelCodeDiagnostics,
    toolsPanelGhciRoot: workspaceWindow.toolsPanelGhci,
    statusbarRoot: workspaceWindow.statusbar,
    statusbarProps: mkStatusbarProps(),
    editorPanel: editorPanel,
    activeEditorFileId: activeEditorFileId,
    pluginState: jsObjectToMap(clientState.pluginState),
    terminals: new Map<TerminalId, Terminal>()
  };

  if (DEMO_MODE) {
    app.toolsPanelTerminalsRoot.appendChild(document.createTextNode(DEMO_MODE_DISABLED_TEXT));
    app.toolsPanelGhciRoot.appendChild(document.createTextNode(DEMO_MODE_DISABLED_TEXT));
  }

  for (const terminalState of clientState.terminals) {
    hexGripAppAddTerminal(app, terminalState.id, terminalState.termSurface);
  }

  app.shortcuts.add([
    {
      shortcut: "Alt+PageDown",
      handler: () => {
        console.log("Next Tab");
        return true;
      }
    },
    {
      shortcut: "Alt+PageUp",
      handler: () => {
        console.log("Prev Tab");
        return true;
      }
    },
    {
      shortcut: "Ctrl+S",
      handler: () => {
        console.log("Save");
        if (app.activeEditorFileId !== null) {
          app.commandSink(
            makeCommand("saveFile", {
              id: app.activeEditorFileId
            })
          );
        }
        return true;
      }
    },
    {
      shortcut: "Ctrl+P",
      handler: () => {
        console.log("Ctrl+P");
        return true;
      }
    },
    {
      shortcut: "Ctrl+Shift+P",
      handler: () => {
        console.log("Ctrl+Shift+P");
        return true;
      }
    },
    {
      shortcut: "Alt+1",
      handler: () => {
        console.log("Tab 1");
        return true;
      }
    },
    {
      shortcut: "Alt+2",
      handler: () => {
        console.log("Tab 2");
        return true;
      }
    }
  ]);

  // Note: This will also call renderToolbar()
  setColorTheme(app, app.colorTheme);

  hexGripAppSetActiveToolsPanelTab(app, app.activeToolsPanelTab);

  renderFileTreePanel(app);
  renderEditorTabs(app);
  renderCodeDiagnosticsPanel(app);
  renderStatusbar(app);

  let codeDiagnostics: CodeDiagnostic[] = [];
  for (const [, value] of Object.entries(clientState.pluginState)) {
    codeDiagnostics = codeDiagnostics.concat(value.codeDiagnostics);
  }
  app.editorPanel.setCodeDiagnostics(codeDiagnostics);

  return app;
}

function jsObjectToMap<T>(obj: { [key: string]: T }): Map<string, T> {
  const result = new Map<string, T>();
  for (const key of Object.keys(obj)) {
    result.set(key, obj[key]);
  }
  return result;
}

function hexGripAppAddTerminal(
  app: HexGripApp,
  terminalId: TerminalId,
  termSurface: TermSurface
): void {
  app.terminals.set(
    terminalId,
    new Terminal(app.toolsPanelTerminalsRoot, termSurface, {
      onKeyPressCommand: (keyPressCommand: KeyPressCommand): void => {
        app.commandSink(
          makeCommand("terminalInputCommand", {
            id: terminalId,
            command: makeTerminalCommand("keyPressCommand", keyPressCommand)
          })
        );
      },
      onResize: (size: TerminalSize): void => {
        app.commandSink(
          makeCommand("terminalInputCommand", {
            id: terminalId,
            command: makeTerminalCommand("terminalSize", size)
          })
        );
      }
    })
  );
}

function setColorTheme(app: HexGripApp, colorTheme: ColorTheme): void {
  saveColorTheme(colorTheme);

  app.colorTheme = colorTheme;

  renderToolbar(app);

  switch (colorTheme) {
    case "light":
      document.body.classList.remove("bp3-dark");
      document.body.style.backgroundColor = "#FFFFFF";
      break;
    case "dark":
      document.body.classList.add("bp3-dark");
      document.body.style.backgroundColor = "#1E1E1E";
      break;
    default:
      assertNever(colorTheme);
  }

  app.editorPanel.setColorTheme(colorTheme);
}

function hexGripAppSetActiveTab(app: HexGripApp, id: EditorFileId): void {
  if (app.activeEditorFileId === id) {
    return;
  }

  app.activeEditorFileId = id;
  app.editorPanel.activateEditor(app.activeEditorFileId);
  renderEditorTabs(app);
}

function hexGripAppSetActiveToolsPanelTab(
  app: HexGripApp,
  tab: ToolsPanelTabs.TabIdentifier
): void {
  app.activeToolsPanelTab = tab;
  renderToolsPanelTabs(app);

  app.toolsPanelTerminalsRoot.style.visibility = "hidden";
  app.toolsPanelCodeDiagnosticsRoot.style.visibility = "hidden";
  app.toolsPanelGhciRoot.style.visibility = "hidden";

  switch (tab) {
    case "TERMINAL":
      app.toolsPanelTerminalsRoot.style.visibility = "visible";
      break;
    case "DIAGNOSTICS":
      app.toolsPanelCodeDiagnosticsRoot.style.visibility = "visible";
      break;
    case "GHCI":
      app.toolsPanelGhciRoot.style.visibility = "visible";
      break;
    default:
      return assertNever(tab);
  }
}

export function hexGripAppClose(app: HexGripApp): void {
  if (app.root.parentElement !== null) {
    app.root.parentElement.removeChild(app.root);
  }
}

function hexgripShowGlobalNotificationBanner<T extends Element>(
  app: HexGripApp,
  element: JSX.Element
): void {
  ReactDOM.render(element, app.globalNotificationBanner);
}

export function hexGripAppEvent(app: HexGripApp, appEvent: AppEvent): void {
  console.log(appEvent.kind);
  switch (appEvent.kind) {
    case "fileTreeViewRefreshed":
      return handleFileTreeViewRefreshed(app, appEvent.value);
    case "editorFileOpened":
      return handleEditorFileOpened(app, appEvent.value);
    case "editorTabClosed":
      return handleEditorTabClosed(app, appEvent.value);
    case "editorFileBufferChanged":
      return handleEditorFileBufferChanged(app, appEvent.value);
    case "editorFileSyntaxHighlighting":
      return handleEditorFileSyntaxHighlighting(app, appEvent.value);
    case "pluginCodeDiagnosticsUpdate":
      return handlePluginCodeDiagnosticsUpdate(app, appEvent.value);
    case "infoRequestRegistration":
      return handleInfoRequestRegistration(app, appEvent.value);
    case "infoRequestResultEvent":
      return handleInfoRequestResultEvent(app, appEvent.value);
    case "terminalCreated":
      return handleTerminalCreated(app, appEvent.value);
    case "terminalOutputEvent":
      return handleTerminalOutputEvent(app, appEvent.value);
    default:
      return assertNever(appEvent);
  }
}

function handleFileTreeViewRefreshed(app: HexGripApp, value: FileTreeViewRefreshed): void {
  app.fileTreeView = value.fileTreeView;
  renderFileTreePanel(app);
}

function handleEditorFileOpened(app: HexGripApp, value: EditorFileOpened): void {
  app.editorTabs.push({
    id: value.editorFile.id,
    filePath: value.editorFile.filePath
  });
  app.editorPanel.addEditor(value.editorFile);
  hexGripAppSetActiveTab(app, value.editorFile.id);
}

function handleEditorTabClosed(app: HexGripApp, value: EditorTabClosed): void {
  app.editorTabs = app.editorTabs.filter((v) => v.id !== value.id);
  // TODO If activeTab was closed then change activeTab
  renderEditorTabs(app);
  app.editorPanel.closeEditor(value.id);
}

function handleEditorFileBufferChanged(app: HexGripApp, value: EditorFileBufferChanged): void {
  //throw new Error("TODO");
}

function handleEditorFileSyntaxHighlighting(
  app: HexGripApp,
  value: EditorFileSyntaxHighlighting
): void {
  app.editorPanel.setSyntaxHighlighting(value.id, value.version, value.spans);
}

function handlePluginCodeDiagnosticsUpdate(
  app: HexGripApp,
  value: PluginCodeDiagnosticsUpdate
): void {
  const getPlugin = (name: string): PluginState => {
    const plugin = app.pluginState.get(name);
    if (plugin !== undefined) {
      return plugin;
    } else {
      const newPlugin: PluginState = {
        workspaceVersion: value.workspaceVersion,
        codeDiagnostics: [],
        complete: false
      };
      app.pluginState.set(name, newPlugin);
      return newPlugin;
    }
  };

  const plugin = getPlugin(value.pluginName);
  if (value.workspaceVersion === plugin.workspaceVersion) {
    plugin.codeDiagnostics = plugin.codeDiagnostics.concat(value.codeDiagnostics);
  } else {
    plugin.codeDiagnostics = value.codeDiagnostics;
  }
  plugin.workspaceVersion = value.workspaceVersion;
  plugin.complete = value.complete;

  renderCodeDiagnosticsPanel(app);
  app.editorPanel.setCodeDiagnostics(plugin.codeDiagnostics);
}

function handleInfoRequestRegistration(app: HexGripApp, value: InfoRequestRegistration): void {
  // TODO ...
}

function handleInfoRequestResultEvent(app: HexGripApp, value: InfoRequestResultEvent): void {
  if (app.openEditorContextPopup !== null) {
    if (value.id === app.openEditorContextPopup.infoRequestId) {
      app.openEditorContextPopup.editorContextPopup.setInfoData({
        infoRequestResult: value.infoRequestResult
      });
    } else {
      // Ignore (received old InfoRequest response)
    }
  }
}

function handleTerminalCreated(app: HexGripApp, value: TerminalCreated): void {
  hexGripAppAddTerminal(app, value.id, value.termSurface);
}

function handleTerminalOutputEvent(app: HexGripApp, value: TerminalOutputEvent): void {
  const terminal = app.terminals.get(value.id);
  if (terminal === undefined) {
    throw new Panic(`Terminal not found`, { terminalId: value.id });
  }

  terminal.processTermSurfaceChange(value.termSurfaceChange);
}
